import * as React from 'react';

import './Modal.scss';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { noop } from '@deltasierra/utilities-object';
import { usePrevious, useDelayedFlag } from '@deltasierra/react-hooks';

export type DSModalProps = {
    /*
     * String className to be applied to the document.body (must be a constant string). Defaults to 'modal-open'.
     * When set to null it doesn't add any class to document.body.
     */
    bodyOpenClassName?: string;

    children?: React.ReactNode;

    /* Number indicating the milliseconds to wait before closing the modal. Defaults to 500 (half-second). */
    closeTimeoutMS?: number;

    /* String className to be applied to the modal content. */
    contentClassName?: string;

    /* String indicating how the content container should be announced to screenreaders. */
    contentLabel?: string;

    /* Object indicating styles to be used for the modal content. */
    contentStyle?: React.CSSProperties;

    /* String className to be applied to the modal dialog. */
    dialogClassName?: string;

    /* Object indicating styles to be used for the modal dialog. */
    dialogStyle?: React.CSSProperties;

    /* String className to be applied to the modal fade. */
    fadeClassName?: string;

    /* Object indicating styles to be used for the modal fade. */
    fadeStyle?: React.CSSProperties;

    /* String className to be applied to the document.html (must be a constant string). Defaults to ''. */
    htmlOpenClassName?: string;

    /* String value of an id attribute to be applied to the modal content */
    id?: string;

    /* Function that will be run right after the modal has opened. */
    onAfterOpen?: () => void;

    /* Function that will be run right before the modal has closed. */
    onBeforeClose?: () => void;

    /* Function that will be run when the modal is requested to be closed, prior to actually closing. */
    onRequestClose?: (event: React.KeyboardEvent | React.MouseEvent | null) => void;

    /* String className to be applied to the overlay. */
    overlayClassName?: string;

    /* Object indicating styles to be used for the modal overlay. */
    overlayStyle?: React.CSSProperties;

    /* Boolean describing if the modal should be shown or not. Defaults to false. */
    show: boolean;

    /*
     * String indicating the role of the modal, allowing the 'dialog' role to be applied if desired.
     * Defaults to "dialog".
     */
    role?: string;

    /*
     * Set this to properly hide your application from assistive screenreaders
     * and other assistive technologies while the modal is open.
     */
    appElement?: HTMLElement;

    /* Boolean indicating if the appElement should be hidden. Defaults to true. */
    ariaHideApp?: boolean;

    /* Boolean indicating if the modal should be focused after render. Defaults to true */
    shouldFocusAfterRender?: boolean;

    /* Boolean indicating if the overlay should close the modal. Defaults to true. */
    shouldCloseOnOverlayClick?: boolean;

    /* Boolean indicating if pressing the esc key should close the modal */
    shouldCloseOnEsc?: boolean;

    /* Boolean indicating if the modal should restore focus to the element that had focus prior to its display. */
    shouldReturnFocusAfterClose?: boolean;

    /* Function that will be called to get the parent element that the modal will be attached to. */
    parentSelector?: () => HTMLElement;

    /* Function accepting the ref for the content */
    contentRef?: (instance: HTMLDivElement) => void;

    /* Function accepting the ref for the overlay */
    overlayRef?: (instance: HTMLDivElement) => void;
};

export type UseShownProps = Pick<DSModalProps, 'onAfterOpen' | 'onBeforeClose' | 'onRequestClose' | 'show'> & {
    children?: React.ReactNode;
    delay?: number;
};

export const useShown = ({ delay = 500, onAfterOpen = noop, onBeforeClose = noop, show }: UseShownProps): boolean => {
    const [shown, setShown] = React.useState(show);
    const onAfterOpenRef = React.useRef(onAfterOpen);
    const onBeforeCloseRef = React.useRef(onBeforeClose);

    React.useEffect(() => {
        onAfterOpenRef.current = onAfterOpen;
    }, [onAfterOpen]);

    React.useEffect(() => {
        onBeforeCloseRef.current = onBeforeClose;
    }, [onBeforeClose]);

    React.useEffect(() => {
        let timeout: number;
        if (show && !shown) {
            setShown(true);
            onAfterOpenRef.current();
        } else if (!show && shown) {
            timeout = window.setTimeout(() => {
                onBeforeCloseRef.current();
                setShown(false);
            }, delay);
        }
        return () => window.clearTimeout(timeout);
    }, [delay, show, shown]);

    return shown;
};

export const DSModal: React.FC<DSModalProps> = props => {
    const {
        closeTimeoutMS = 250,
        onAfterOpen = noop,
        onBeforeClose = noop,
        onRequestClose = noop,
        shouldCloseOnOverlayClick,
        show,
    } = props;
    const shown = useDelayedFlag({
        falseDelay: closeTimeoutMS,
        onFalse: onBeforeClose,
        onTrue: onAfterOpen,
        trueDelay: 0,
        value: show,
    });
    const previousBodyClass = usePrevious(props.bodyOpenClassName);
    const previousHtmlClass = usePrevious(props.htmlOpenClassName);
    const [fadeIn, setFadeIn] = React.useState<'' | 'in'>('');

    const sendRequestClose = React.useCallback(
        (event: React.KeyboardEvent | React.MouseEvent) => {
            if (shouldCloseOnOverlayClick !== false) {
                onRequestClose(event);
            }
        },
        [onRequestClose, shouldCloseOnOverlayClick],
    );

    React.useEffect(() => {
        if (shown) {
            setFadeIn('in');
        }
    }, [shown]);

    React.useEffect(() => {
        if (!show) {
            setFadeIn('');
        }
    }, [show]);

    React.useEffect(() => {
        if (previousBodyClass) {
            document.body.classList.remove(previousBodyClass);
        }
    }, [previousBodyClass]);

    React.useEffect(() => {
        if (props.bodyOpenClassName !== previousBodyClass || props.htmlOpenClassName !== previousHtmlClass) {
            document.body.classList.remove(previousBodyClass || 'modal-open');
            if (previousHtmlClass) {
                document.body.classList.remove(previousHtmlClass || '');
            }
        }
        document.body.classList.toggle(props.bodyOpenClassName || 'modal-open', shown);
        if (previousHtmlClass) {
            document.body.classList.toggle(props.htmlOpenClassName || '', shown);
        }
    }, [previousBodyClass, previousHtmlClass, props.bodyOpenClassName, props.htmlOpenClassName, shown]);

    const overlayClassName = classNames(
        props.overlayClassName,
        'modal-backdrop-opacity-override',
        'modal-backdrop',
        'fade',
        fadeIn,
    );

    return createPortal(
        <>
            {shown && (
                <>
                    <div
                        className={overlayClassName}
                        style={{ ...props.overlayStyle, display: 'block' }}
                        onClick={sendRequestClose}
                    />
                    <div
                        className={classNames(props.fadeClassName, 'modal', 'fade', fadeIn)}
                        style={{ ...props.fadeStyle, display: 'block' }}
                    >
                        <div
                            aria-label={props.contentLabel}
                            className={classNames(props.dialogClassName, 'modal-dialog')}
                            id={props.id}
                            role={props.role || 'dialog'}
                            style={props.dialogStyle}
                            onClick={event => event.stopPropagation()}
                        >
                            <div
                                className={classNames(props.contentClassName, 'modal-content')}
                                style={props.contentStyle}
                            >
                                {props.children}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>,
        document.body,
    );
};

export type DSModalHeaderProps = {
    children?: React.ReactNode;
    title?: string;
};

export const DSModalHeader: React.FunctionComponent<DSModalHeaderProps> = ({ children, title }) => (
    <div className="modal-header branded-alternate">
        {title && (
            <h3 className="modal-title">
                <p>{title}</p>
            </h3>
        )}
        {!title && children}
    </div>
);

export type DSModalBodyProps = {
    children?: React.ReactNode;
};
export const DSModalBody: React.FC<DSModalBodyProps> = ({ children }) => <div className="modal-body">{children}</div>;

export type DSModalFooterProps = {
    children?: React.ReactNode;
};
export const DSModalFooter: React.FC<DSModalFooterProps> = ({ children }) =>
    <div className="modal-footer">{children}</div>;
